/**
 * AuthContext — single source of truth for JWT + user identity.
 *
 * Flow:
 *   1. On mount we call POST /api/luna/auth/telegram with
 *      `initData` from window.Telegram.WebApp.
 *      - Production: initData is signed by Telegram; backend verifies.
 *      - Test mode (no initData available, e.g. desktop browser):
 *        we fall back to `devTelegramId = 592457038` (admin) which
 *        the backend accepts when `test_mode=true`.
 *   2. On success we store the token in memory + localStorage and
 *      inject it into the api axios instance via setAuthToken().
 *   3. A follow-up GET /api/luna/auth/me fetches the full profile
 *      (balance, isAdmin, etc.) which is exposed via `useAuth()`.
 *   4. On 401 from any subsequent call we drop the token and
 *      re-trigger the login flow so the session recovers silently.
 *
 * The token is persisted in localStorage under `luna_jwt` so a
 * refresh of the mini-app doesn't force re-authentication. Telegram
 * is the signing authority — losing the token just means we do one
 * extra round-trip, never a forced re-login prompt.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, lunaApi, setAuthToken, type MeResponse } from '@/services/api';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';

const TOKEN_KEY = 'luna_jwt';
const ADMIN_DEV_TELEGRAM_ID = 592457038;

export interface AuthUser {
  id: number;
  telegramId: number;
  username: string | null;
  isAdmin: boolean;
  usdtBalance: string;
  ofertaVersionAccepted: string | null;
  ofertaAcceptedAt: string | null;
}

export type AccessState = 'not_requested' | 'pending' | 'blocked' | 'outdated_link';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  token: string | null;
  /** Когда бэк вернул 403 ACCESS_NOT_REQUESTED / PENDING / BLOCKED — показать плашку. */
  accessState: AccessState | null;
  /** Manual refresh — pulls /auth/me again */
  refresh: () => Promise<void>;
  /** Wipe local token and trigger a fresh login */
  logout: () => void;
  /** Юзер нажал «Запросить доступ» — шлёт POST /auth/request-access */
  requestAccess: () => Promise<void>;
  /** Идёт ли сейчас requestAccess — для disabled состояния кнопки */
  requestingAccess: boolean;
  /**
   * Optimistic update: добавить delta к usdtBalance локально, не дожидаясь
   * следующего /me. Используется после успешного claim/p2p чтобы баланс
   * в хедере прыгал мгновенно, а не через 30с polling.
   * Следующий refresh() перезапишет значение из бэкенда.
   */
  applyBalanceDelta: (delta: number) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { initData } = useTelegramWebApp();
  const [token, setToken] = useState<string | null>(() => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  });
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessState, setAccessState] = useState<AccessState | null>(null);
  const [requestingAccess, setRequestingAccess] = useState(false);
  const unauthorizedHandlerRef = useRef<(() => void) | null>(null);

  // Persist token helper — also updates the axios default header
  const persistToken = useCallback((next: string | null) => {
    setToken(next);
    setAuthToken(next);
    try {
      if (next) localStorage.setItem(TOKEN_KEY, next);
      else localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* Silent — Telegram WebView may block storage */
    }
  }, []);

  // Step 1 — exchange initData for a JWT
  const login = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Реф-код из URL query (?ref=XXX или ?start_param=XXX) — бот
      // передаёт его юзеру через web_app: url, и мы проксируем на бэк.
      // initData.start_param работает только при ?startapp= entry,
      // а кнопка из бота использует web_app — поэтому дублируем в body.
      const urlParams = new URLSearchParams(window.location.search);
      const refFromUrl =
        urlParams.get('ref') ||
        urlParams.get('start_param') ||
        urlParams.get('startapp') ||
        null;

      const body: {
        initData?: string;
        devTelegramId?: number;
        startParam?: string;
      } = {};
      if (initData) {
        body.initData = initData;
      } else {
        body.devTelegramId = ADMIN_DEV_TELEGRAM_ID;
      }
      if (refFromUrl) {
        body.startParam = refFromUrl;
      }

      const res = await api.post<{ success: boolean; token: string }>(
        '/auth/telegram',
        body,
      );
      if (res.data.success && res.data.token) {
        persistToken(res.data.token);
        setAccessState(null);
      } else {
        throw new Error('Auth response missing token');
      }
    } catch (err) {
      // Распознаём access-gate ошибки по коду из ответа бэкенда.
      const errAny = err as {
        response?: { data?: { error?: string } };
        message?: string;
      };
      const code = errAny?.response?.data?.error;
      if (code === 'ACCESS_NOT_REQUESTED') {
        setAccessState('not_requested');
        setError(null);
      } else if (code === 'ACCESS_PENDING') {
        setAccessState('pending');
        setError(null);
      } else if (code === 'ACCESS_BLOCKED') {
        setAccessState('blocked');
        setError(null);
      } else if (code === 'OUTDATED_LINK') {
        setAccessState('outdated_link');
        setError(null);
      } else {
        const message =
          err instanceof Error ? err.message : 'Не удалось авторизоваться';
        setError(message);
        setAccessState(null);
      }
      persistToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [initData, persistToken]);

  // Step 2 — fetch /auth/me using whatever token is currently active
  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const res = await lunaApi.me();
      const data = res.data as MeResponse;
      if (data.success) {
        setUser(data.user);
        setError(null);
      }
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response
        ?.status;
      if (status === 401) {
        // Token expired — drop it and try to re-login
        persistToken(null);
        setUser(null);
        await login();
      } else {
        const message =
          err instanceof Error ? err.message : 'Не удалось получить профиль';
        setError(message);
      }
    }
  }, [token, login, persistToken]);

  // On mount — sync persisted token, then ensure we have one
  useEffect(() => {
    setAuthToken(token);
    if (token) {
      // Have a token — fetch /me
      void refresh().finally(() => setLoading(false));
    } else {
      // No token — try to log in
      void login();
    }
    // Intentionally only run on token change (login triggers a reload anyway)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Install a single 401 handler on the axios instance so every failed
  // request automatically triggers a relogin. We stash the cleanup in
  // a ref so StrictMode double-renders don't stack interceptors.
  useEffect(() => {
    const interceptorId = api.interceptors.response.use(
      (response) => response,
      async (err: { response?: { status?: number } }) => {
        if (err?.response?.status === 401) {
          if (!unauthorizedHandlerRef.current) {
            unauthorizedHandlerRef.current = () => {
              persistToken(null);
              void login();
            };
            unauthorizedHandlerRef.current();
            // Clear the ref after a short delay so a new 401 storm
            // can still trigger one more attempt later.
            window.setTimeout(() => {
              unauthorizedHandlerRef.current = null;
            }, 2000);
          }
        }
        return Promise.reject(err);
      },
    );
    return () => {
      api.interceptors.response.eject(interceptorId);
    };
  }, [login, persistToken]);

  const logout = useCallback(() => {
    persistToken(null);
    setUser(null);
    void login();
  }, [persistToken, login]);

  // Optimistic balance — прибавляем delta к locally stored usdtBalance.
  // Следующий refresh() выровняет с бэком. Нужно для мгновенного
  // отображения в хедере после claim/p2p — чтобы юзер не ждал polling.
  const applyBalanceDelta = useCallback((delta: number) => {
    if (!isFinite(delta) || delta === 0) return;
    setUser((prev) => {
      if (!prev) return prev;
      const current = parseFloat(prev.usdtBalance || '0') || 0;
      const next = Math.max(0, current + delta);
      return { ...prev, usdtBalance: next.toFixed(8) };
    });
  }, []);

  // Пользователь явно нажал «Запросить доступ».
  // Шлём POST /auth/request-access c тем же initData что и при логине,
  // бэк переводит его из 'not_requested' → 'pending', уведомляет
  // админа. После успеха переводим UI в состояние 'pending'.
  const requestAccess = useCallback(async () => {
    setRequestingAccess(true);
    setError(null);
    try {
      const body: { initData?: string; devTelegramId?: number } = {};
      if (initData) {
        body.initData = initData;
      } else {
        body.devTelegramId = ADMIN_DEV_TELEGRAM_ID;
      }
      await api.post<{ success: boolean; status: string }>(
        '/auth/request-access',
        body,
      );
      setAccessState('pending');
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Не удалось отправить заявку';
      setError(msg);
    } finally {
      setRequestingAccess(false);
    }
  }, [initData]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user, loading, error, token, accessState,
      refresh, logout, requestAccess, requestingAccess,
      applyBalanceDelta,
    }),
    [
      user, loading, error, token, accessState,
      refresh, logout, requestAccess, requestingAccess,
      applyBalanceDelta,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
